import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { SearchTabs } from './components/SearchTabs'
import { CustomerSearch } from './components/CustomerSearch'
import { DossierSearch } from './components/DossierSearch'
import { ExpertisesList } from './components/ExpertisesList'
import { useCustomerSearch } from './hooks/useCustomerSearch'
import { useDossierSearch } from './hooks/useDossierSearch'
import { Customer } from './types/search.types'
import './styles/search.css'
import './styles/SearchLayout.css'

export const SearchTab: React.FC = () => {
  const location = useLocation()
  const [activeTab, setActiveTab] = useState<'customer' | 'dossier'>('customer')
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)

  const {
    customers,
    loading: customerLoading,
    error: customerError,
    searchCustomers,
    searchQuery: customerSearchQuery,
    hasSearched,
    setSearchQuery: setCustomerSearchQuery
  } = useCustomerSearch()

  const {
    affaire,
    affaires,
    matchType,
    loading: dossierLoading,
    error: dossierError,
    searchDossier,
    searchQuery: dossierSearchQuery,
    setSearchQuery: setDossierSearchQuery
  } = useDossierSearch()

  // Handle navigation from payment tab or expertise detail
  useEffect(() => {
    // If navigating back with a selected customer (from affaire list)
    if (location.state?.selectedCustomer) {
      setSelectedCustomer(location.state.selectedCustomer)
      setActiveTab('customer')
      setCustomerSearchQuery(location.state.searchName || '')
    } else if (location.state?.searchName) {
      // Regular search navigation
      if (location.state?.tab === 'customer') {
        setActiveTab('customer')
        setCustomerSearchQuery(location.state.searchName)
        // Auto-search after setting the query
        setTimeout(() => {
          searchCustomers(location.state.searchName)
        }, 100)
      } else if (location.state?.tab === 'dossier') {
        setActiveTab('dossier')
        setDossierSearchQuery(location.state.searchName)
        // Auto-search after setting the query
        setTimeout(() => {
          searchDossier(location.state.searchName)
        }, 100)
      }
    }
  }, [location.key, searchCustomers, searchDossier])

  const handleBackToSearch = () => {
    setSelectedCustomer(null)
  }

  // Show expertise list if customer is selected
  if (selectedCustomer) {
    return (
      <ExpertisesList
        customer={selectedCustomer}
        onBack={handleBackToSearch}
      />
    )
  }

  return (
    <div className="search-page-container">
      <div className="search-header">
        <div className="search-header-content">
          <div className="search-title-row">
            <div>
              <h1 className="search-title">Gestion des Clients et Dossiers</h1>
              <p className="search-subtitle">Recherchez et gérez vos clients et dossiers d'expertise</p>
            </div>
            <div className="search-toggle-wrapper">
              <SearchTabs activeTab={activeTab} onTabChange={setActiveTab} />
            </div>
          </div>
        </div>
      </div>
      
      <div className="search-content">
        <div className="search-content-inner">
          {activeTab === 'customer' ? (
            <CustomerSearch
              customers={customers}
              loading={customerLoading}
              error={customerError}
              searchQuery={customerSearchQuery}
              hasSearched={hasSearched}
              onSearch={searchCustomers}
              onSelectCustomer={setSelectedCustomer}
            />
          ) : (
            <DossierSearch
              affaire={affaire}
              affaires={affaires}
              matchType={matchType}
              loading={dossierLoading}
              error={dossierError}
              searchQuery={dossierSearchQuery}
              onSearch={searchDossier}
              onSetSearchQuery={setDossierSearchQuery}
            />
          )}
        </div>
      </div>
    </div>
  )
}